import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { resolveTemplateLineage, templateLineageFrom, type TemplateLineage } from '@/modules/templateScenes/templateSubmitService';
import { fetchTemplateUsageSummary } from '@/infrastructure/clients/creatorApi';
import { storyWorkspace } from '@/modules/storyData/storyWorkspace';
import type { TemplateUsageSummary } from '@/types/templateSubmissionTypes';

const RETRY_MS = 3_000;

export function useTemplateStatus(sceneOriginId: string | undefined, userId: string | undefined) {
  const services = useServices();
  const [lineage, setLineage] = useState<TemplateLineage | null>(null);
  const [usage, setUsage] = useState<TemplateUsageSummary | null>(null);
  const [usageFailed, setUsageFailed] = useState(false);
  const [lineageFailed, setLineageFailed] = useState(false);
  const seq = useRef(0);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const latestRefresh = useRef<(retry?: boolean) => Promise<void>>(async () => {});

  const [lineageFor, setLineageFor] = useState<{ sceneOriginId?: string; userId?: string } | null>(null);
  if (!lineageFor || lineageFor.sceneOriginId !== sceneOriginId || lineageFor.userId !== userId) {
    setLineageFor({ sceneOriginId, userId });
    setLineage(null);
    setLineageFailed(false);
  }

  // The first read of a story takes the template from the story page's one read, when that answered; any other is its own
  const refresh = useCallback((retry = true, first = false) => {
    clearTimeout(retryTimer.current);
    if (!sceneOriginId || !userId) return Promise.resolve();
    const n = ++seq.current;
    const read = () => resolveTemplateLineage(services, sceneOriginId, userId);
    const fromPage = first ? storyWorkspace.take(sceneOriginId, 'templateStatus') : null;
    const lineageRead = fromPage
      ? fromPage.then(({ data, error }) => (error ? read() : templateLineageFrom(data.templateStatus)))
      : read();
    return lineageRead
      .then(next => {
        if (n !== seq.current) return;
        setLineage(next);
        setLineageFailed(false);
      })
      .catch(error => {
        console.error('Template status read failed', error);
        if (n !== seq.current) return;
        setLineageFailed(true);
        if (retry) retryTimer.current = setTimeout(() => void latestRefresh.current(false), RETRY_MS);
      });
  }, [sceneOriginId, userId, services]);

  useLayoutEffect(() => {
    latestRefresh.current = refresh;
  });

  useEffect(() => {
    void refresh(true, true);
    return () => clearTimeout(retryTimer.current);
  }, [refresh]);

  const status = lineage?.submission?.status;
  const templateOriginId = lineage?.templateSceneOriginId;
  const showsUsage = status === 'accepted' && !!templateOriginId;
  const [usageFor, setUsageFor] = useState<{ status: typeof status; originId: typeof templateOriginId } | null>(null);
  if (!usageFor || usageFor.status !== status || usageFor.originId !== templateOriginId) {
    setUsageFor({ status, originId: templateOriginId });
    if (!showsUsage) {
      setUsage(null);
      setUsageFailed(false);
    }
  }
  useEffect(() => {
    if (!showsUsage || !templateOriginId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // The first read takes the usage from the story page's one read, which holds it for an accepted template
    const fromPage = sceneOriginId ? storyWorkspace.take(sceneOriginId, 'templateUsage') : null;
    const usageOf = (first: boolean): Promise<TemplateUsageSummary | null> => {
      if (!first || !fromPage) return fetchTemplateUsageSummary(templateOriginId);
      return fromPage.then(({ data, error }) => {
        const status = error ? null : data.templateStatus;
        if (status?.templateStoryUniqueId !== templateOriginId || status.submission?.status !== 'accepted') return fetchTemplateUsageSummary(templateOriginId);
        return status.usage ? { qualifyingUseCount: status.usage.qualifyingUseCount, totalUseCount: status.usage.totalUseCount } : null;
      });
    };
    const read = (retry: boolean, first = false) =>
      usageOf(first).then(
        (summary) => {
          if (cancelled) return;
          setUsage(summary);
          setUsageFailed(false);
        },
        (error) => {
          console.error('Template usage read failed', error);
          if (cancelled) return;
          setUsageFailed(true);
          if (retry) timer = setTimeout(() => void read(false), RETRY_MS);
        },
      );
    void read(true, true);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [showsUsage, templateOriginId, sceneOriginId]);

  return { lineage, lineageFailed: lineageFailed && !lineage, usage, usageFailed, refresh };
}
